import type { BaseLayerTypeEnumType, SectorTypeEnumType } from '@forgd/shared/enums'
import { BaseLayerOptions } from '@forgd/shared/dropdowns'

export interface CoinGeckoSelection {
  id?: string | null
  name: string
  image?: string | null
  symbol?: string
}

/** User chose "not listed yet" in CoinGeckoSearch — id is explicitly null. */
export const COINGECKO_NOT_LISTED_SELECTION = {
  id: null,
  name: 'Not Listed',
} as const satisfies CoinGeckoSelection

/**
 * Maps CoinGeckoSearch v-model to a coingeckoId for form state.
 * - Cleared (null/undefined) → null (avoid stale ids on submit)
 * - Not listed (id === null) → null
 * - Coin picked → id string
 */
export function coingeckoIdFromSelection(
  selection: CoinGeckoSelection | null | undefined,
): string | null {
  return selection?.id ?? null
}

export interface CoinGeckoCoinDetails {
  id: string
  name: string
  symbol: string
  image: string
  homepage: string | null
  baseLayerId: string | null
  tgeDate: string | null
}

export interface CoinGeckoTokenFields {
  coingeckoId: string
  name: string
  ticker: string
  image: string
  baseLayer: BaseLayerTypeEnumType[]
  sector?: SectorTypeEnumType[]
  link?: string
  tgeDate?: string
}

export function findBaseLayerByCoingeckoId(id: string): (typeof BaseLayerOptions)[number] | undefined {
  return BaseLayerOptions.find(
    option =>
      option.coingeckoPlatformIds?.includes(id) ||
      option.coingeckoId === id ||
      option.value.toLowerCase() === id.toLowerCase(),
  )
}

export function tokenFieldsFromCoinGecko(coin: CoinGeckoCoinDetails): CoinGeckoTokenFields {
  const baseLayer = findBaseLayerByCoingeckoId(coin.baseLayerId ?? coin.id)

  const fields: CoinGeckoTokenFields = {
    coingeckoId: coin.id,
    name: coin.name,
    ticker: coin.symbol.toUpperCase(),
    image: coin.image,
    baseLayer: [(baseLayer?.value ?? 'Other') as BaseLayerTypeEnumType],
  }

  if (!coin.baseLayerId) {
    fields.sector = ['Base Layer']
  }

  if (coin.homepage) {
    fields.link = coin.homepage
  }

  if (coin.tgeDate) {
    fields.tgeDate = coin.tgeDate
  }

  return fields
}
